import { cn } from "@/lib/utils";
import Adjustment from "@/types/serializers/Adjustment";
import { Link } from "@inertiajs/react";
import { CheckIcon } from "lucide-react";
import { GoDotFill } from "react-icons/go";
import { buttonVariants } from "./ui/button";

type Step = "property" | "data" | "comps" | "trend" | "summary";
type Props = {
  adjustment?: Adjustment;
  currentStep: Step;
  className?: string;
};

function deriveSteps(adjustment: Adjustment | undefined): {
  step: Step;
  label: string;
  status?: "disabled" | "unsaved" | "completed" | "started" | null;
  href?: string;
}[] {
  const adjustmentId = adjustment?.public_id;
  return [
    // this supports a new adjustment and editing an existing adjustment
    {
      step: "property",
      label: "1. Subject Property",
      status: adjustment?.subject_page_status,
      href: adjustmentId ? `/adjustments/${adjustmentId}/property` : "/adjustments/new",
    },
    // the rest of these are only relevant once an adjustment exists
    {
      step: "data",
      label: "2. Trend Data",
      status: adjustment?.data_page_status,
      href: adjustmentId && `/adjustments/${adjustmentId}/data`,
    },
    {
      step: "comps",
      label: "3. Comparables",
      status: adjustment?.comps_page_status,
      href: adjustmentId && `/adjustments/${adjustmentId}/comps`,
    },
    {
      step: "trend",
      label: "4. Market Trend",
      status: adjustment?.trend_page_status,
      href: adjustmentId && `/adjustments/${adjustmentId}/trend`,
    },
    {
      step: "summary",
      label: "5. Adjustment Summary",
      status: adjustment?.summary_page_status,
      href: adjustmentId && `/adjustments/${adjustmentId}/summary`,
    },
  ];
}

function DisabledStep({ children }: { children: React.ReactNode }) {
  return (
    <div className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium text-secondary-foreground h-9 px-4 py-2 cursor-default">
      {children}
    </div>
  );
}

export default function AdjustmentSteps({ adjustment, currentStep, className }: Props) {
  return (
    <div className={cn("flex gap-2 lg:justify-center -mx-4 overflow-x-scroll py-2", className)}>
      {deriveSteps(adjustment).map((step) => {
        const isCurrentStep = step.step === currentStep;
        if (!step.href || (!isCurrentStep && (step.status === null || step.status === undefined))) {
          return <DisabledStep key={step.step}>{step.label}</DisabledStep>;
        }

        const classForStep = cn(
          buttonVariants({
            variant: isCurrentStep ? "secondary" : "ghost",
          }),
          {
            "text-muted-foreground": step.status === "completed",
          },
        );

        return (
          <Link key={step.step} className={classForStep} href={step.href}>
            {step.label}
            {step.status === "completed" && <CheckIcon className="h-4 w-4" />}
            {step.status === "started" && <GoDotFill className="h-4 w-4 opacity-15" />}
            {step.status === "unsaved" && <GoDotFill className="h-4 w-4 opacity-15" />}
          </Link>
        );
      })}
    </div>
  );
}
